import {SOURCE_SYNOP, SOURCE_WEATHERFLOW} from '../../api';
import {StationMeta, WeatherStationFromList} from '../../api/types';

export const getCustomStationName = (s: WeatherStationFromList) =>
  (s.link && s.link?.user_data?.name) || s.custom_name || '';

export const getStationName = (s: WeatherStationFromList) => {
  if (s) {
    return (
      (s.link && (s.link?.user_data?.name || s.link.name)) ||
      s.custom_name ||
      s.name
    );
  }
  return '';
};

/**
 * Whether the owner has hidden this linked station from their lists.
 *
 * Only linked stations can be hidden, and only the owner's `user_data.hidden`
 * says so. Reads through `?.` on purpose: `user_data` is absent entirely on
 * links rehydrated from a persisted store written before the backend grew the
 * field (SMARTMIXIN-IOS-6R), and an absent key means "no user overrides", i.e.
 * not hidden. Every hidden/visible split goes through here so the guard cannot
 * drift back out of one of the call sites.
 */
export const isStationHidden = (s: WeatherStationFromList) =>
  Boolean(s.link && s.link.user_data?.hidden);

/**
 * Whether we positively know this station's provider account is disconnected.
 *
 * Deliberately `=== false` rather than `!account_active`: the flag is absent on
 * links rehydrated from a store predating it, and null for a station with no
 * provider account, and neither of those means "inactive" — a truthiness test
 * showed a disconnected-account banner on stations that were perfectly fine.
 * Same rehydration hazard as `isStationHidden` (SMARTMIXIN-IOS-6R).
 */
export const isProviderAccountInactive = (s: WeatherStationFromList) =>
  s?.link?.account_active === false;

export const isWeatherflowRainCheckEnabled = (meta: StationMeta | undefined) =>
  meta?.weatherflow_raincheck_enabled !== false;

/**
 * Whether the station's hourly history buckets are true aggregations of
 * sub-hourly samples. All provider stations report every few minutes, so
 * the backend computes per-hour min/max for them; SYNOP hourly rows are
 * single point observations.
 */
export const hasAggregatedHourlyHistory = (s: WeatherStationFromList) =>
  s.source !== SOURCE_SYNOP;

export const getRainToday = (s: WeatherStationFromList) => {
  if (s) {
    if (
      s.source === SOURCE_WEATHERFLOW &&
      isWeatherflowRainCheckEnabled(s.meta) &&
      !isNaN(Number(s.last_measure?.RR_TODAY_RAIN_CHECK))
    ) {
      // Weatherlink Rain Check
      return s.last_measure?.RR_TODAY_RAIN_CHECK;
    }
    if (!isNaN(Number(s.last_measure?.RR_TODAY))) {
      return s.last_measure?.RR_TODAY;
    }
    if (!isNaN(Number(s.last_measure?.RR_PIEZO_TODAY))) {
      return s.last_measure?.RR_PIEZO_TODAY;
    }
  }
  return null;
};

export const getRainYesterday = (s: WeatherStationFromList) => {
  if (s) {
    if (
      s.source === SOURCE_WEATHERFLOW &&
      isWeatherflowRainCheckEnabled(s.meta) &&
      !isNaN(Number(s.last_measure?.RR_YESTERDAY_RAIN_CHECK))
    ) {
      // Weatherlink Rain Check
      return s.last_measure?.RR_YESTERDAY_RAIN_CHECK;
    }
    if (!isNaN(Number(s.last_measure?.RR_YESTERDAY))) {
      return s.last_measure?.RR_YESTERDAY;
    }
  }
  return null;
};
